import Navbar from "../components/NavbarM";
import React, { useEffect, useMemo, useState } from "react";
import { Card, Badge, Button, Spinner } from "react-bootstrap";
import {
  Clock,
  ArrowUpCircle,
  ArrowDownCircle,
  ReceiptText,
  CalendarDays,
  Store,
} from "lucide-react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import "./TransactionHistoryPage.css";

function TransactionHistoryPage() {
  const storeId = localStorage.getItem("storeId");
  const storeName = localStorage.getItem("storeName");

  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) {
      alert("Store not logged in. Please sign in again.");
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "transactions"),
      where("storeId", "==", storeId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort newest first in JS to avoid Firestore index issues
        list.sort((a, b) => {
          const dateA = a.timestamp?.toDate?.() || new Date(0);
          const dateB = b.timestamp?.toDate?.() || new Date(0);
          return dateB - dateA;
        });

        setTransactions(list);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching transactions:", err);
        alert("Could not load transactions.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [storeId]);

  const filteredTransactions = useMemo(() => {
    if (filter === "all") return transactions;

    const now = new Date();

    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - 7);

    return transactions.filter((tx) => {
      if (!tx.timestamp) return false;

      const txDate = tx.timestamp.toDate();

      if (filter === "today") {
        return txDate >= startOfToday;
      }

      if (filter === "week") {
        return txDate >= startOfWeek;
      }

      return true;
    });
  }, [transactions, filter]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "Pending...";
    const date = timestamp.toDate();

    return date.toLocaleString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const maskCard = (cardNumber) => {
    if (!cardNumber) return "7069********3011";
    return String(cardNumber).replace(/^(\d{4})\d+(\d{4})$/, "$1********$2");
  };

  const totalEarned = filteredTransactions
    .filter((tx) => tx.type === "add")
    .reduce((sum, tx) => sum + Number(tx.points || 0), 0);

  const totalRedeemed = filteredTransactions
    .filter((tx) => tx.type === "redeem")
    .reduce((sum, tx) => sum + Math.abs(Number(tx.points || 0)), 0);

  return (
    <>
      <Navbar />

      <div className="history-page">
        <div className="history-container">
          <section className="history-hero">
            <div>
              <div className="history-pill">
                <ReceiptText size={18} />
                Store Transactions
              </div>

              <h1>Transaction History</h1>
              <p>Review earned and redeemed points for {storeName || "this store"}.</p>
            </div>

            <div className="history-store-card">
              <Store size={22} />
              <span>{storeName || "Store"}</span>
            </div>
          </section>

          <section className="history-summary-grid">
            <div className="history-summary-card">
              <span>Total Records</span>
              <h2>{filteredTransactions.length}</h2>
            </div>

            <div className="history-summary-card earned">
              <span>Earned</span>
              <h2>+{totalEarned}</h2>
            </div>

            <div className="history-summary-card redeemed">
              <span>Redeemed</span>
              <h2>{totalRedeemed}</h2>
            </div>
          </section>

          <section className="history-filter-card">
            <div>
              <h3>Filter Activity</h3>
              <p>Select a time range to view transactions.</p>
            </div>

            <div className="history-filters">
              <Button
                className={filter === "today" ? "filter-btn active" : "filter-btn"}
                onClick={() => setFilter("today")}
              >
                Today
              </Button>

              <Button
                className={filter === "week" ? "filter-btn active" : "filter-btn"}
                onClick={() => setFilter("week")}
              >
                This Week
              </Button>

              <Button
                className={filter === "all" ? "filter-btn active" : "filter-btn"}
                onClick={() => setFilter("all")}
              >
                All
              </Button>
            </div>
          </section>

          {loading ? (
            <div className="history-loading">
              <Spinner animation="border" variant="warning" />
              <p>Loading transactions...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="empty-history-card">
              <CalendarDays size={44} />
              <h3>No transactions found</h3>
              <p>No activity found for the selected filter.</p>
            </div>
          ) : (
            <div className="history-list">
              {filteredTransactions.map((tx) => (
                <Card
                  key={tx.id}
                  className={`history-item ${
                    tx.type === "add" ? "add-item" : "redeem-item"
                  }`}
                >
                  <div className="history-item-top">
                    <div className="history-member">
                      <div
                        className={`history-icon ${
                          tx.type === "add" ? "add-icon" : "redeem-icon"
                        }`}
                      >
                        {tx.type === "add" ? (
                          <ArrowUpCircle size={24} />
                        ) : (
                          <ArrowDownCircle size={24} />
                        )}
                      </div>

                      <div>
                        <h5>{tx.memberName || tx.name || "Guest"}</h5>
                        <p>{maskCard(tx.cardNumber)}</p>
                      </div>
                    </div>

                    <Badge
                      bg={tx.type === "add" ? "success" : "danger"}
                      className="history-badge"
                    >
                      {tx.type === "add" ? "Added" : "Redeemed"}
                    </Badge>
                  </div>

                  <div className="history-item-bottom">
                    <div>
                      <span>Points</span>
                      <strong
                        className={tx.type === "add" ? "text-success" : "text-danger"}
                      >
                        {tx.points > 0 ? `+${tx.points}` : tx.points}
                      </strong>
                    </div>

                    <div>
                      <span>Balance</span>
                      <strong>{tx.balance} pts</strong>
                    </div>

                    <div className="history-time">
                      <Clock size={15} />
                      <span>{formatDate(tx.timestamp)}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default TransactionHistoryPage;